import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, TrendingUp, Users, Award, Calendar, Target, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';

const ICON_OPTIONS = [
  { value: 'users', label: 'Users', icon: Users },
  { value: 'trending-up', label: 'Trending Up', icon: TrendingUp },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'calendar', label: 'Calendar', icon: Calendar },
  { value: 'target', label: 'Target', icon: Target },
  { value: 'bar-chart', label: 'Bar Chart', icon: BarChart3 },
];

const AdminStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStatsBlocks();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditItem({
      title: '',
      value: '',
      icon: 'trending-up',
      description: '',
      page_id: '',
      order: stats.length,
      is_active: true
    });
    setEditModal(true);
  };

  const openEdit = (stat) => {
    setEditItem({ ...stat });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!editItem.title || !editItem.value) {
      toast.error('Title and Value are required');
      return;
    }

    setSaving(true);
    try {
      if (editItem.id) {
        await adminApi.updateStatsBlock(editItem.id, editItem);
        setStats(stats.map(s => s.id === editItem.id ? editItem : s));
        toast.success('Stats block updated');
      } else {
        const response = await adminApi.createStatsBlock(editItem);
        setStats([...stats, response.data]);
        toast.success('Stats block created');
      }
      setEditModal(false);
    } catch (error) {
      toast.error('Failed to save stats block');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this stats block?')) return;
    try {
      await adminApi.deleteStatsBlock(id);
      setStats(stats.filter(s => s.id !== id));
      toast.success('Stats block deleted');
    } catch (error) {
      toast.error('Failed to delete stats block');
    }
  };

  const getIcon = (iconName) => {
    const found = ICON_OPTIONS.find(i => i.value === iconName);
    if (found) {
      const IconComp = found.icon;
      return <IconComp className="w-8 h-8" />;
    }
    return <TrendingUp className="w-8 h-8" />;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Stats Blocks</h1>
          <p className="text-muted-foreground">Manage statistics and metrics displayed on the site</p>
        </div>
        <Button onClick={openCreate} data-testid="add-stats-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Stats Block
        </Button>
      </div>

      {stats.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No stats blocks yet. Add your first stats block!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.id} className={!stat.is_active ? 'opacity-50' : ''}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className="text-primary">
                  {getIcon(stat.icon)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">{stat.value}</div>
                {stat.description && (
                  <p className="text-sm text-muted-foreground">{stat.description}</p>
                )}
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(stat)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(stat.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem?.id ? 'Edit Stats Block' : 'Add Stats Block'}</DialogTitle>
            <DialogDescription>Fill in the stats block details</DialogDescription>
          </DialogHeader>
          
          {editItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={editItem.title}
                    onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                    placeholder="e.g., Clients Helped"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Value *</Label>
                  <Input
                    value={editItem.value}
                    onChange={(e) => setEditItem({...editItem, value: e.target.value})}
                    placeholder="e.g., 500+"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={editItem.icon}
                  onValueChange={(v) => setEditItem({...editItem, icon: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editItem.description}
                  onChange={(e) => setEditItem({...editItem, description: e.target.value})}
                  rows={2}
                  placeholder="Brief description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={editItem.order}
                    onChange={(e) => setEditItem({...editItem, order: parseInt(e.target.value) || 0})}
                  />
                </div>
                
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={editItem.is_active}
                    onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStats;
